import { useMemo } from "react";
import { useFeature } from "@nextiva/feature-toggle";
import { useGetAdminGroupHierarchy } from "hooks/useAdminGroup/useGetAdminGroupHierarchy";
import {
  collectSubtreeExternalIds,
  HierarchyNode,
} from "hooks/useAdminGroup/useAdminGroupSubtreeIds";
import AppData from "app_data";

export interface HierarchyExternalIds {
  /**
   * externalIds of EVERY node in the admin-group hierarchy (all ADMIN_GROUPs
   * and LOCATIONs, deduped, normalized to strings) -- the whole tree, not a
   * selected subtree. Empty until the hierarchy loads, and empty when LBA is
   * off (the query is disabled).
   */
  allExternalIds: string[];
  /** True once the hierarchy has loaded. Stays false on a failed load. */
  isReady: boolean;
  /** Mirrors useGetAdminGroupHierarchy.isFailed (stable through retries). */
  isFailed: boolean;
}

// The full hierarchy's externalIds, for consumers that need "everything the
// breadcrumb can see" rather than the selected subtree (which
// useAdminGroupSubtreeIds covers). Root selection deliberately collapses to
// NO_SCOPE in useScopedWayfinderSelection, so the scope hooks expose no ids
// at root -- this hook fills that gap for the Analytics Location view's
// degraded-seed fallback. Reuses useGetAdminGroupHierarchy's react-query
// cache (key ["adminGroupHierarchy", corp]) -- no extra fetch.
export const useHierarchyExternalIds = (): HierarchyExternalIds => {
  const currentCorp: string | number | undefined = AppData.user?.enterprise_id;

  const { isEnabled, isReady: isFlagReady } = useFeature({
    name: "Analytics.LocationBasedAdministration",
  });
  const lbaEnabled = isEnabled && isFlagReady;

  const { data: hierarchy, isFailed } = useGetAdminGroupHierarchy({
    corpAccountNbr: String(currentCorp ?? ""),
    isLbaEnabled: lbaEnabled,
  });

  return useMemo<HierarchyExternalIds>(() => {
    if (!hierarchy) {
      return { allExternalIds: [], isReady: false, isFailed: Boolean(isFailed) };
    }

    // The wayfinder tree supports both a single root node and a forest
    // (array of roots) -- handle both shapes.
    const roots: HierarchyNode[] = Array.isArray(hierarchy)
      ? hierarchy
      : [hierarchy as HierarchyNode];

    const ids = new Set<string>();
    for (const root of roots) {
      collectSubtreeExternalIds(root, ids);
    }

    return {
      allExternalIds: Array.from(ids),
      isReady: true,
      isFailed: Boolean(isFailed),
    };
  }, [hierarchy, isFailed]);
};
